import React, { createContext, useContext, useState } from "react";
import type { ReactNode } from "react";

// Types
interface Brand {
  brandId: string;
  brandName: string;
  brandLogoPath: string;
}

interface Project {
  campaignId: number;
  campaignName: string;
}

interface SelectOption {
  label: string;
  value: string;
}

interface AppContextType {
  selectedBrand: Brand | null;
  setSelectedBrand: (brand: Brand | null) => void;
  selectedProject: Project | null;
  setSelectedProject: (project: Project | null) => void;
  selectedChannel: SelectOption | null;
  setSelectedChannel: (channel: SelectOption | null) => void;
  selectedSubChannel: SelectOption | null;
  setSelectedSubChannel: (subChannel: SelectOption | null) => void;
  selectedMarketRegion: SelectOption | null;
  setSelectedMarketRegion: (region: SelectOption | null) => void;
  selectedLanguage: SelectOption | null;
  setSelectedLanguage: (language: SelectOption | null) => void;
  resetAllSelections: () => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [selectedChannel, setSelectedChannel] = useState<SelectOption | null>(null);
  const [selectedSubChannel, setSelectedSubChannel] = useState<SelectOption | null>(null);
  const [selectedMarketRegion, setSelectedMarketRegion] = useState<SelectOption | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<SelectOption | null>(null);

  const resetAllSelections = () => {
    setSelectedBrand(null);
    setSelectedProject(null);
    setSelectedChannel(null);
    setSelectedSubChannel(null);
    setSelectedMarketRegion(null);
    setSelectedLanguage(null);
  };

  const value: AppContextType = {
    selectedBrand,
    setSelectedBrand,
    selectedProject,
    setSelectedProject,
    selectedChannel,
    setSelectedChannel,
    selectedSubChannel,
    setSelectedSubChannel,
    selectedMarketRegion,
    setSelectedMarketRegion,
    selectedLanguage,
    setSelectedLanguage,
    resetAllSelections,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useAppContext = (): AppContextType => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
};

// Export types for use in other files
export type { Brand, Project, SelectOption };
